import type { ReportExport } from '@jaf/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSession } from '@/features/auth';
import { ApiError } from '@/shared/api';
import { notifyError } from '@/shared/ui/notify';
import { fetchReportExportFile } from '../api/report-exports-api';
import { reportExportKeys } from '../api/report-exports-queries';
import { saveBlob } from '../lib/save-blob';

/** Exportações já baixadas (ou baixando) nesta aba: cada arquivo sai uma vez só da API. */
const claimed = new Set<string>();

/** Resultado do download. */
export type DownloadOutcome = 'SAVED' | 'SKIPPED' | 'FAILED';

/**
 * Baixa o arquivo de uma exportação pronta (o token vai no cabeçalho, não na URL). A API apaga
 * a exportação no download, então cada uma é baixada uma vez: pedidos repetidos são ignorados,
 * e a que outra aba já baixou (404) some sem aviso de erro.
 */
export function useDownloadReportExport(): (
  reportExport: ReportExport,
) => Promise<DownloadOutcome> {
  const organizationId = useSession((state) => state.session?.organizationId ?? '');
  const queryClient = useQueryClient();
  return useCallback(
    async (reportExport: ReportExport) => {
      if (claimed.has(reportExport.id)) return 'SKIPPED';
      claimed.add(reportExport.id);
      let outcome: DownloadOutcome = 'SAVED';
      try {
        saveBlob(await fetchReportExportFile(reportExport), reportExport.fileName);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          outcome = 'SKIPPED';
        } else {
          claimed.delete(reportExport.id);
          notifyError(error, 'Não foi possível baixar o arquivo');
          outcome = 'FAILED';
        }
      }
      await queryClient.invalidateQueries({ queryKey: reportExportKeys.all(organizationId) });
      return outcome;
    },
    [organizationId, queryClient],
  );
}
